import { ref } from 'vue'
import { useApiClient } from '@/composables/useApiClient'
import type { Template, TemplateOption, TemplateSendCheckResponse } from '../types/interfaces'
import type { IPaginationMeta, IPaginationResponse } from '@/services/interfaces/pagination-response.interface'

const templates = ref<Template[]>([])
const templatesMeta = ref<IPaginationMeta>({
  total: 0,
  page: 1,
  size: 10,
  pages: 1,
})
const isLoading = ref(false)
const templatesByCampaign = new Map<string, IPaginationResponse<Template>>()

export const useTemplateService = () => {
  const apiClient = useApiClient(true)

  const fetchTemplates = async (params?: {
    campaign_id?: string
    status?: 'APPROVED' | 'PENDING' | 'REJECTED'
    template_name?: string
    page?: number
    size?: number
  }): Promise<Template[]> => {
    isLoading.value = true

    try {
      const response = await apiClient.get<{
        data: Template[]
        meta: {
          total: number
          page: number
          size: number
          pages: number
        }
      }>('/templates', { params })

      templates.value = response.data
      return templates.value
    } catch (error) {
      console.error('Error fetching templates:', error)
      return []
    } finally {
      isLoading.value = false
    }
  }

  const fetchTemplatesByCampaign = async (
    campaignId: string,
    options?: {
      force?: boolean
      search?: string | null
      page?: number
      size?: number
      category?: string | null
    },
  ): Promise<Template[]> => {
    if (!campaignId) {
      templates.value = []
      templatesMeta.value = { total: 0, page: 1, size: options?.size ?? 10, pages: 1 }
      return []
    }

    const params = {
      search: options?.search?.trim() || undefined,
      // 'ALL' se omite: el backend no filtra cuando no llega el parametro.
      category: options?.category && options.category !== 'ALL' ? options.category : undefined,
      page: options?.page ?? 1,
      size: options?.size ?? 10,
    }
    const cacheKey = `${campaignId}:${params.search ?? ''}:${params.category ?? ''}:${params.page}:${params.size}`

    if (!options?.force && templatesByCampaign.has(cacheKey)) {
      const cached = templatesByCampaign.get(cacheKey)!
      templates.value = cached.data
      templatesMeta.value = cached.meta
      return templates.value
    }

    isLoading.value = true

    try {
      const response = await apiClient.get<IPaginationResponse<Template>>(
        `/templates/by-campaign/${encodeURIComponent(campaignId)}`,
        { params },
      )
      templatesByCampaign.set(cacheKey, response)
      templates.value = response.data
      templatesMeta.value = response.meta
      return response.data
    } catch (error) {
      console.error('Error fetching templates by campaign:', error)
      templates.value = []
      // Sin esto queda el meta del fetch anterior y el paginador sigue
      // ofreciendo paginas de una lista que ya no esta.
      templatesMeta.value = { total: 0, page: 1, size: params.size, pages: 1 }
      return []
    } finally {
      isLoading.value = false
    }
  }

  const fetchTemplateOptions = async (params?: {
    status?: 'APPROVED' | 'PENDING' | 'REJECTED'
  }): Promise<TemplateOption[]> => {
    try {
      const response = await apiClient.get<TemplateOption[]>('/templates/options', { params })
      return response
    } catch (error) {
      console.error('Error fetching template options:', error)
      return []
    }
  }

  const fetchTemplateById = async (id: string): Promise<Template | null> => {
    try {
      const response = await apiClient.get<Template>(`/templates/${id}`)
      return response
    } catch (error) {
      console.error('Error fetching template by ID:', error)
      return null
    }
  }

  const checkTemplateSendReservation = async (params: {
    phone: string
    /** BSUID del contacto sin teléfono; el cupo se busca por cualquiera de los dos. */
    bsuid?: string
    template_id: string
    user_id: string
    account_id?: string | null
  }): Promise<TemplateSendCheckResponse | null> => {
    try {
      return await apiClient.get<TemplateSendCheckResponse>('/oper/whatsapp/template-send-check', {
        params,
      })
    } catch (error) {
      console.error('Error checking template send reservation:', error)
      // pre-chequeo es cortesía de UX: si falla, el envío sigue y el backend
      // revalida (evento TEMPLATE_SEND_BLOCKED si el chat está reservado)
      return null
    }
  }

  const getTemplateById = (id: string): Template | undefined => {
    return templates.value.find((t) => t.id === id)
  }

  const getTemplateByName = (name: string): Template | undefined => {
    return templates.value.find((t) => t.content.name === name)
  }

  return {
    templates,
    templatesMeta,
    isLoading,
    fetchTemplates,
    fetchTemplatesByCampaign,
    fetchTemplateOptions,
    fetchTemplateById,
    checkTemplateSendReservation,
    getTemplateById,
    getTemplateByName,
  }
}
